import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

interface SignatureResponse {
  signatureUrl: string | null;
  hasSignature: boolean;
  /**
   * True when the signature was lazily replicated from another site the
   * user belongs to and has not yet been explicitly confirmed here.
   */
  needsConfirmation: boolean;
}

export interface ISignatureService {
  getMySignature(): Promise<SignatureResponse>;
  getSignatureKey(): Promise<{ storageKey: string | null }>;
  saveSignature(dataUrl: string): Promise<SignatureResponse>;
  deleteSignature(): Promise<void>;
  /** Reaffirms a replicated signature for the current site. Idempotent. */
  confirmSignature(): Promise<SignatureResponse>;
}

@Injectable()
class SignatureService implements ISignatureService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  async getMySignature(): Promise<SignatureResponse> {
    return this.httpService.get<SignatureResponse>('signature/me');
  }

  async getSignatureKey(): Promise<{ storageKey: string | null }> {
    return this.httpService.get<{ storageKey: string | null }>('signature/me/key');
  }

  async saveSignature(dataUrl: string): Promise<SignatureResponse> {
    return this.httpService.post<SignatureResponse>('signature', { dataUrl });
  }

  async deleteSignature(): Promise<void> {
    await this.httpService.delete('signature');
  }

  async confirmSignature(): Promise<SignatureResponse> {
    return this.httpService.post<SignatureResponse>('signature/confirm');
  }
}

export { type SignatureResponse, SignatureService };
